/**
 * Direct-to-GCS uploads through a URL the builder API signed.
 *
 * The signature covers the request headers (Content-Type and the size range), so
 * the PUT must carry exactly the values the API signed. The API returns them as
 * `upload_headers`; this sends them verbatim rather than rebuilding them from a
 * constant. The hero-image upload used to hard-code a 50 MiB range against the
 * server's 25 MiB, so GCS answered every PUT with 403 SignatureDoesNotMatch.
 */

export interface SignedUploadTarget {
  upload_url: string;
  upload_headers?: Record<string, string>;
}

/** The headers to send, or an error if the API did not say what they are. */
export function signedUploadHeaders(target: SignedUploadTarget): Record<string, string> {
  const headers = target.upload_headers;
  if (!headers || Object.keys(headers).length === 0) {
    // Guessing would reproduce the 403; say what is actually wrong instead.
    throw new Error('Upload is not available right now. Refresh the page and try again.');
  }
  return headers;
}

export async function putToSignedUrl(
  target: SignedUploadTarget,
  file: Blob,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(target.upload_url, {
    method: 'PUT',
    headers: signedUploadHeaders(target),
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status}). Try a smaller image.`);
  }
}
